import React, { useEffect, useRef, useState, useCallback } from "react";
import { Card, Spinner, Col, Row, Form } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  generateTaskHtml,
  filterSubtasksMap,
  handleSendProposal,
  fetchTemplateSections,
  EmailConfigRequiredModal,
  getTranslatedSalutation
} from "../../../assets/js/common";
import axios from "axios";
import "assets/css/template.css";
import "@fortawesome/fontawesome-free/css/all.min.css"; // Import Font Awesome
import EditableSectionModal from "../proposalModals/pditableSectionModal";
import IconButton from "components/common/iconButton";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { handleUploadPDF } from "components/proposals/proposalPdfMethods/handleProposalPdf";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import PDFVierwer from "../pdfVierwer";
import { getDomainConfig } from "../../../config/domainConfig";

const TemplateViewerAdmin = ({
  formData,
  workType,
  logoSrc,
  handleCloseProposal,
  refreshProposalData
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templateConfig, setTemplateConfig] = useState(null);
  const [sectionMap, setSectionMap] = useState({});
  const [values, setValues] = useState({});
  const [proposalTaskData, setProposalTaskData] = useState([]);
  const [task, setTask] = useState([]); // assume you’ll fetch this
  const [subtasksMap, setSubtasksMap] = useState({});
  const [templateTaskContent, setTemplateTaskContent] = useState("");
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingKey, setEditingKey] = useState(""); // which field is being edited
  const [editorValue, setEditorValue] = useState(""); // current content in modal
  const invoiceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [borderVisible, setBorderVisible] = useState(true); // if needed
  const signatureRefClient = useRef(null);
  const [clientSignature, setClientSignature] = useState("");
  const [isClientSignatureBtnDisabled, setIsClientSignatureBtnDisabled] = useState(false);
  const [isEmailButtonDisabled, setIsEmailButtonDisabled] = useState(false);
  const isApproved = formData?.proposal.Status === "1";
  const proposalDocumentDetail = formData?.proposalDetailsData || {};
  const relationData = formData?.relationData || {};
  const companySignature = proposalDocumentDetail?.CompanySignature || null;
  const [comment, setComment] = useState("");
  const isImportedProposal = formData?.proposal?.IsImported === true;
  const importedPdfUrl = formData?.proposalDetailsData?.Document;
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const showGlobalLoader = !isImportedProposal && isLoading;
  const [showEmailConfigRequiredModal, setShowEmailConfigRequiredModal] = useState(false);
  const [emailModalType, setEmailModalType] = useState("email");
  const { financialLabels: finLabels } = getDomainConfig();

  const fetchPdfBlob = async (blobUrl) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/blobRoute/getBlob`,
      {
        params: {
          blobUrl
        },
        responseType: "blob",
        headers: {
          Authorization:
            process.env.REACT_APP_BasicAuthSecret
        }
      }
    );

    return response.data;
  };

  useEffect(() => {
    const loadImportedPdf = async () => {
      if (!isImportedProposal || !importedPdfUrl || pdfUrl) return;
      setPdfLoading(true);

      const blob = await fetchPdfBlob(importedPdfUrl);
      const url = URL.createObjectURL(blob);

      setPdfUrl(url);
    };

    loadImportedPdf();
  }, [isImportedProposal, importedPdfUrl, pdfUrl]);

  useEffect(() => {
    if (formData?.proposal?.ClientComment) {
      setComment(formData.proposal.ClientComment);
    }
  }, [formData]);

  useEffect(() => {
    const customerId = formData?.proposal?.CustomerId;
    const proposalId = formData?.proposal?.ProposalId;
    const templateId = formData?.proposal?.TemplateId;
    const isAdminView = true;
    if (customerId && workType) {
      fetchTemplateSections(
        customerId,
        templateId,
        workType,
        setTemplateConfig,
        setSectionMap,
        setValues,
        formData?.proposalDetailsData,
        proposalId,
        formData?.proposal?.CurrentVersionId,
        isAdminView
      );
    }
  }, [formData?.proposal?.CustomerId, workType, formData?.proposal?.ProposalId]);

  useEffect(() => {
    const preloaded = formData?.preloadedTasks;
    if (Array.isArray(preloaded) && preloaded.length > 0) {
      setProposalTaskData(preloaded);
      setTask(
        preloaded.map((t) => ({
          TaskId: t.TaskId,
          TaskName: formData?.preloadedTaskNameById?.[t.TaskId] || "Unnamed Task",
        }))
      );
      setSubtasksMap(formData?.preloadedAllSubtasksByTaskId || {});
      return;
    }

    fetchProposalTask(formData?.proposal?.ProposalId);
  }, [formData?.proposal?.ProposalId, formData?.preloadedTasks]);

  const fetchProposalTask = async (proposalId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setProposalTaskData(response.data);
    } catch (error) {
      console.error("Error fetching proposal task data:", error);
    }
  };

  useEffect(() => {
    if (Array.isArray(formData?.preloadedTasks) && formData.preloadedTasks.length > 0) {
      return;
    }

    const fetchAllSubtasks = async () => {
      const results = await Promise.all(
        proposalTaskData.map(async (task) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
              headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret
              }
            }
            );
            return [task.TaskId, response.data];
          } catch (error) {
            console.error(
              `Error fetching subtasks for task ${task.TaskId}`,
              error
            );
            return null;
          }
        })
      );

      const allSubtasks = {};
      results.filter(Boolean).forEach(([taskId, subtasks]) => {
        allSubtasks[taskId] = subtasks;
      });
      setSubtasksMap(allSubtasks);
    };

    if (proposalTaskData && proposalTaskData.length > 0) {
      fetchTask();
      fetchAllSubtasks();
    }
  }, [proposalTaskData, formData?.preloadedTasks]);

  const fetchTask = async () => {
    const customerId = formData?.proposal.CustomerId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/taskRoute/tasks`,
        {
          params: { customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setTask(response.data);
    } catch (error) {
      console.error("Error fetching task data:", error);
    }
  };

  useEffect(() => {
    if (proposalTaskData && proposalTaskData.length > 0 && task.length > 0) {
      const selectedSubtasks = Array.isArray(formData.selectedSubtasks)
        ? formData.selectedSubtasks
        : [];

      const filteredMap = filterSubtasksMap(subtasksMap, selectedSubtasks);

      const tasksHtml = generateTaskHtml(
        proposalTaskData,
        task,
        filteredMap,
        selectedSubtasks,
        t,
        formData?.proposal?.TemplateName
      );

      setTemplateTaskContent(tasksHtml);
    }
  }, [
    proposalTaskData,
    task,
    subtasksMap,
    formData.selectedSubtasks,
    t,
    workType,
    formData?.proposal?.TemplateName
  ]);

  useEffect(() => {
    const clientSig = formData?.proposalDetailsData?.ClientSignature;
    if (!clientSig) return;

    setClientSignature(clientSig);
    setIsClientSignatureBtnDisabled(true);

    if (isImportedProposal) return;

    if (!isApproved) {
      loadAndDrawSignature(clientSig, signatureRefClient);
    }
  }, [
    formData?.proposalDetailsData?.ClientSignature,
    isApproved,
    isImportedProposal
  ]);

  const renderSection = (key) => {
    const section = sectionMap[key];
    if (!section) return null;

    if (section.type === "dynamic") {
      switch (section.source) {
        case "introTextBlock":
          const introText = t(`introTextBlock.introText`, {
            StreetName: formData?.relationData?.StreetName || "",
            PostCode: formData?.relationData?.PostCode || "",
            City: formData?.relationData?.City || "",
          });

          return (
            <p key={key} className="fs-10 mb-3">
              {introText}
            </p>
          );

        case "timeScheduleText": {
          const { startDate, endDate, startTime } = formData || {};
          if (!startDate) return null;

          const formattedStartDate = moment(startDate).format("DD-MM-YYYY");
          const formattedStartTime = startTime
            ? moment(startTime).format("HH:mm")
            : "";
          const formattedEndDate = endDate
            ? moment(endDate).format("DD-MM-YYYY")
            : "";

          let timeText = "";

          if (startTime && endDate) {
            timeText = t("timeScheduleTextWithEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
              EndDate: formattedEndDate,
            });
          } else if (startTime) {
            timeText = t("timeScheduleTextWithoutEnd", {
              StartDate: formattedStartDate,
              StartTime: formattedStartTime,
            });
          } else {
            timeText = t("timeScheduleTextOnlyDate", {
              StartDate: formattedStartDate,
            });
          }

          return (
            <p key={key} className="fs-10 mb-3">
              {timeText}
            </p>
          );
        }

        case "proposalTasksBlock":
          if (!templateTaskContent?.trim()) return null;
          return (
            <div className="fs-10 mb-3" key={key}>
              <span dangerouslySetInnerHTML={{ __html: templateTaskContent }} />
            </div>
          );

        case "pageBreakAfterExtraContent":
          return (
            <div
              className="text-muted text-center"
              style={{
                margin: "40px 0",
                borderTop: "2px dashed #aaa",
                position: "relative",
                fontSize: "12px",
                color: "#777",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "-10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#fff",
                  padding: "0 8px",
                }}
              >
                Page Break
              </span>
            </div>
          );

        default:
          return null;
      }
    }

    if (section.type === "editable") {
      const isEmpty =
        !values[section.key] ||
        values[section.key].replace(/<[^>]*>/g, "").trim() === "";

      if (isEmpty) return null;

      return (
        <div key={section.key} className="mb-3">
          {section.label_key && (
            <p className="mb-1 fs-10">
              <strong>{t(section.label_key)}:</strong>
              <br />
            </p>
          )}

          <div className="commonStyles fs-10">
            <div
              id={section.key}
              dangerouslySetInnerHTML={{
                __html: values[section.key],
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // Function to draw signature from state
  const drawSignature = useCallback((signature, signatureRef) => {
    if (signature && signatureRef.current) {
      const canvas = signatureRef.current;
      canvas.clear();
      canvas.fromDataURL(`data:image/png;base64,${signature}`);
    }
  }, []);

  // Handle window resize and redraw signature
  useEffect(() => {
    const handleResize = () => {
      drawSignature(clientSignature, signatureRefClient);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clientSignature, drawSignature]);

  const loadAndDrawSignature = (signatureData, signatureRef) => {
    if (!signatureData) return;

    const canvas = signatureRef?.current;

    if (!canvas || typeof canvas.fromDataURL !== "function") {
      return;
    }

    canvas.fromDataURL(`data:image/png;base64,${signatureData}`);
  };


  if (!isImportedProposal && (!templateConfig || !Object.keys(sectionMap).length)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const renderLine = (text) => {
    if (!text) return null;

    let cleaned = String(text).trim();

    // remove leading hyphens like "-- ", "--- ", etc.
    cleaned = cleaned.replace(/^[-\s]+/, "");

    // remove placeholders or empty lines
    if (!cleaned) return null;

    return (
      <>
        {cleaned}
        <br />
      </>
    );
  };

  const getPagesFromSections = () => {
    const pages = [];
    let current = [];

    templateConfig.sections.forEach((sectionKey) => {
      if (sectionKey === "pageBreakAfterExtraContent") {
        pages.push([...current]);
        current = [];
      } else {
        current.push(sectionKey);
      }
    });

    if (current.length > 0) {
      pages.push(current);
    }

    return pages;
  };

  // Function to update proposal status
  const updateProposalStatus = async (status, comment) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/proposalRoute/updateProposal-status/${proposalDocumentDetail.ProposalId}`,
        { Status: status, Comment: comment },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
    } catch (error) {
      console.error("Proposal Status Error:", error);
    }
  };

  const sendApprovedorRejectedEmail = async (status) => {
    const proposalValidationPath = `/validate-proposal/${formData.proposalDetailsData.ProposalDetailId}`;

    try {
      const domain = process.env.REACT_APP_DEFAULT_DOMAIN;
      await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendProposalApprovalEmail`,
        {
          to: relationData.Emailaddress1,
          contactperson: relationData.Contactperson,
          locationAddress: `${formData.locationData.StreetName} ${formData.locationData.HouseNumber}`,
          locationTown: `${formData.locationData.PostCode} ${formData.locationData.City}`,
          proposalType: formData.proposal.TemplateName,
          documentLink: proposalValidationPath,
          culture: localStorage.getItem("culture") || "nl",
          communicationUserId: proposalDocumentDetail.CommunicationUserId,
          domain,
          status,
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const successMessage =
        status === "approved" ? t("approvalEmailSent") : t("declineEmailSent");
      toast.success((successMessage), { position: "top-right", autoClose: 2000 });
    } catch (error) {
      console.error("Error sending proposal email:", error);
      const errorMessage =
        status === "approved"
          ? t("errorSendingApprovalEmail")
          : t("errorSendingDeclineEmail");
      toast.error((errorMessage), { position: "top-right", autoClose: 2000 });
    }
  };

  const sendProposal = async () => {
    if (isEmailButtonDisabled) return;
    setIsEmailButtonDisabled(true);

    try {
      const isAlreadyApproved = formData?.proposal?.Status === "1";

      const hasSignature =
        isImportedProposal ||
        isAlreadyApproved ||
        !!companySignature;

      if (!hasSignature) {
        setEmailModalType("signature");
        setShowEmailConfigRequiredModal(true);
        setIsEmailButtonDisabled(false);
        return;
      }

      // Save company signature (normal flow)
      if (!isImportedProposal && !isAlreadyApproved) {
        const companySignatureData = {
          CompanySignature: companySignature,
          CompanySignatureDate: new Date().toISOString(),
        };

        await axios.patch(
          `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/company-signature/${proposalDocumentDetail.ProposalDetailId}`,
          companySignatureData,
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
          }
        );
      }

      // Already approved → resend email
      if (isAlreadyApproved) {
        await sendApprovedorRejectedEmail("approved");
        return;
      }

      // Normal send
      const domain = process.env.REACT_APP_DEFAULT_DOMAIN;

      await handleSendProposal(
        { ...proposalDocumentDetail },
        formData,
        relationData,
        t,
        setIsEmailButtonDisabled,
        handleCloseProposal,
        domain
      );

      await updateProposalStatus(3, formData?.proposal?.ClientComment);

      if (typeof refreshProposalData === "function") {
        refreshProposalData();
      }

      // toast.success(t("proposalSentSuccess"), { position: "top-right", autoClose: 2000 });
    } catch (error) {
      console.error("Error sending proposal:", error);
      toast.error(t("proposalSentError"), { position: "top-right", autoClose: 2000 });
    } finally {
      setIsEmailButtonDisabled(false);
    }
  };

  return (
    <>
      {isImportedProposal && (
        <div style={{ position: "relative" }}>

          {pdfLoading && (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          <PDFVierwer
            pdfUrl={pdfUrl}
            onRendered={() => setPdfLoading(false)}
          />
        </div>
      )}

      {/* loader */}
      {showGlobalLoader && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255, 255, 255, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">{t("savingProposalPleaseWait")}</p>
          </div>
        </div>
      )}

      {!isImportedProposal && (
        <div ref={invoiceRef}>
          {getPagesFromSections().map((sectionKeys, pageIndex, allPages) => (
            <Card key={pageIndex} className="mb-3" style={{ marginTop: 0 }}>
              <Card.Body>
                {/* First page: render header */}
                {pageIndex === 0 && (
                  <>
                    <Row className="align-items-center text-center mb-3">
                      <Col sm={6} className="text-sm-start">
                        {logoSrc && (
                          <img
                            src={logoSrc}
                            alt="invoice"
                            width={150}
                            style={{
                              border: "none",
                              boxShadow: "none",
                              background: "transparent",
                            }}
                          />
                        )}
                      </Col>
                      <Col className="text-sm-end mt-3 mt-sm-0">
                        {/* Address block */}
                        <p className="fs-10 mb-0">
                          {renderLine(
                            [
                              formData?.settings?.OrganizationName
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                          {renderLine(
                            [
                              formData?.locationData?.StreetName,
                              formData?.locationData?.HouseNumber,
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                          {renderLine(
                            [
                              formData?.locationData?.PostCode,
                              formData?.locationData?.City,
                            ]
                              .filter(Boolean)
                              .join(" ")
                          )}
                        </p>
                        <p className="fs-10">
                          {renderLine(formData?.userData?.PhoneNumber)}
                          {renderLine(formData?.userData?.Emailaddress)}
                          {formData?.settings?.DomainName !== "app.getplan.nl" &&
                            renderLine(`www.${formData?.settings?.DomainName}`)}
                        </p>
                      </Col>
                    </Row>

                    <Row className="align-items-end">
                      <Col xs={12} sm={8} className="text-sm-start mb-3 mb-sm-0">
                        <p className="fs-10 mb-0">
                          {renderLine(formData?.relationData?.Name)}
                          {renderLine(getTranslatedSalutation(formData?.relationData, t))}
                          {renderLine(
                            [
                              formData?.relationData?.StreetName,
                              formData?.relationData?.PostCode,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          )}
                          {renderLine(
                            [
                              formData?.relationData?.City,
                              formData?.relationData?.Country,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          )}
                          {renderLine(formData?.relationData?.Emailaddress1)}
                        </p>
                      </Col>
                      <Col xs={12} sm={4} className="text-sm-end mb-3 mb-sm-0">
                        <p className="fs-10 mb-0">
                          {t("date")},{" "}
                          {formData?.proposalDetailsData.Date
                            ? moment(formData.proposalDetailsData.Date).format(
                              "DD-MM-YYYY"
                            )
                            : moment().format("DD-MM-YYYY")}
                        </p>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <p className="fs-10 mb-2">
                        {formData?.relationData?.Salutation || formData?.relationData?.FirstName
                          ? t("dear")
                          : t("dearSirMadam")}{" "}
                        {getTranslatedSalutation(formData?.relationData, t)},
                      </p>
                    </div>
                  </>
                )}

                {/* Page content */}
                {sectionKeys.map(renderSection)}

                {/* Footer: only on last page */}
                {pageIndex === allPages.length - 1 && (
                  <>
                    <p className="fs-10 mb-2">{t("signedCopyReturn")}</p>
                    <p className="fs-10 mb-2">{t("greeting")}</p>

                    {/* Signature Block */}
                    <div className="mt-4 mb-2 fs-10">
                      <Row className="mt-3">
                        {/* Company Signature */}
                        <Col xs={12} md={6} className="text-left order-1 order-md-1">
                          {formData?.proposalDetailsData?.CompanySignature ? (
                            <>
                              <img
                                src={`data:image/png;base64,${formData?.proposalDetailsData?.CompanySignature}`}
                                alt={t("companySignature")}
                                className="p-2 mt-2"
                                style={{
                                  width: "150px",
                                  height: "60px",
                                  objectFit: "contain",
                                }}
                              />
                              <p className="fs-10 mb-0">
                                <strong>
                                  {t("dateOfSigning")}:{" "}
                                  {formData?.proposalDetailsData
                                    ?.CompanySignatureDate
                                    ? moment(
                                      formData?.proposalDetailsData
                                        ?.CompanySignatureDate
                                    ).format("DD-MM-YYYY")
                                    : moment().format("DD-MM-YYYY")}
                                </strong>
                              </p>
                            </>
                          ) : (
                            <>
                              <h6>{t("companySignature")}</h6>
                              <div
                                style={{
                                  paddingTop: "20px",
                                  color: "#666",
                                  fontStyle: "italic",
                                }}
                              >
                                <p className="mb-2">
                                  _______________________________
                                </p>
                                <p className="mb-2">
                                  <small>{t("noSignatureAvailable")}</small>
                                </p>
                              </div>
                            </>
                          )}

                          {/* Company Address */}
                          <p className="fs-10 mt-2 mb-0">
                            {formData?.userData.Name || "--"},
                            <br />
                            {formData?.settings?.OrganizationName || "--"}
                            <br />
                            {formData?.locationData?.City || "--"}
                          </p>
                        </Col>

                        {/* Client Signature */}
                        <Col xs={12} md={6} className="text-left order-2 order-md-2 mt-3">
                          {formData?.proposalDetailsData?.ClientSignature ? (
                            <>
                              <img
                                src={`data:image/png;base64,${formData?.proposalDetailsData?.ClientSignature}`}
                                alt={t("customerSignature")}
                                className="p-2 mt-2"
                                style={{
                                  width: "150px",
                                  height: "60px",
                                  objectFit: "contain",
                                }}
                              />
                              {formData?.proposalDetailsData
                                ?.ClientSignatureDate && (
                                  <p className="fs-10 mb-0">
                                    <strong>
                                      {t("dateOfSigning")}:{" "}
                                      {formData?.proposalDetailsData
                                        ?.ClientSignatureDate
                                        ? moment(
                                          formData?.proposalDetailsData
                                            ?.ClientSignatureDate
                                        ).format("DD-MM-YYYY")
                                        : null}
                                    </strong>
                                  </p>
                                )}
                            </>
                          ) : (
                            <>
                              <h6>{t("customerSignature")}</h6>
                              <div
                                style={{
                                  paddingTop: "20px",
                                  color: "#666",
                                  fontStyle: "italic",
                                }}
                              >
                                <p className="mb-2">
                                  _______________________________
                                </p>
                                <p className="mb-2">
                                  <small>
                                    {t("signatureAndDateWillAppearHere")}
                                  </small>
                                </p>
                              </div>
                            </>
                          )}

                          {/* Client Address */}
                          <p className="fs-10 mt-2 mb-0">
                            {getTranslatedSalutation(formData?.relationData, t)}
                            <br />
                            {formData?.relationData?.Name || ""}
                            <br />
                            {formData?.relationData?.City || ""}
                          </p>
                        </Col>
                      </Row>
                    </div>

                    {/* Footer details — domain based */}
                    <div className="fs-10 text-end mt-5 text-muted"
                      style={{ fontSize: "12px", opacity: 0.7 }}>
                      <p className="mb-0">
                        {!finLabels.showIfsc && (
                          <>
                            <strong>{t("iban")}:</strong> {formData?.locationData?.Iban ?? "--"}
                            <br />
                            <strong>{t("gAccount")}:</strong> {formData?.locationData?.GAccount ?? "--"}
                            <br />
                            <strong>{t("chamberOfCommerce")}:</strong> {formData?.locationData?.KvKDetails ?? "--"}
                            <br />
                            <strong>{t("vatNumber")}:</strong> {formData?.locationData?.BTWDetails ?? "--"}
                          </>
                        )}
                        {finLabels.showIfsc && (
                          <>
                            <strong>{t(finLabels.account)}:</strong> {formData?.locationData?.AccountNumber ?? "--"}
                            <br />
                            <strong>{t(finLabels.ifsc)}:</strong> {formData?.locationData?.IfscCode ?? "--"}
                            <br />
                            <strong>{t(finLabels.gst)}:</strong> {formData?.locationData?.GstDetails ?? "--"}
                          </>
                        )}
                      </p>
                    </div>
                    <Row className="mt-4 mb-2">
                      <Col>
                        <Form.Group controlId="opmerking">
                          <Form.Label>{t("commentLabel")}</Form.Label>
                          <p className="form-control-plaintext fs-10">
                            {comment || "-"}
                          </p>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <hr className="m-0"></hr>

      {/* Action Buttons */}
      <Card className="mt-2 no-shadow">
        <Card.Body className="p-2">
          <Row className="justify-content-between align-items-center mb-3">
            <Col md>
              <h5 className="mb-2 mb-md-0"></h5>
            </Col>
            <Col xs="auto">
              <IconButton
                size="sm"
                icon="times"
                className="me-1 mb-2 mb-sm-0"
                iconClassName="me-1"
                onClick={handleCloseProposal}
                variant="danger"
                disabled={formData?.isLoading}
              >
                {t("cancel")}
              </IconButton>

              <IconButton
                size="sm"
                className="me-1 mb-2 mb-sm-0"
                iconClassName="me-1"
                icon="envelope"
                disabled={
                  isEmailButtonDisabled ||
                  (isImportedProposal && pdfLoading)
                }
                onClick={sendProposal}
              >
                {t("sendProposal")}
              </IconButton>

            </Col>
          </Row>
        </Card.Body>
      </Card>
      <EmailConfigRequiredModal
        show={showEmailConfigRequiredModal}
        onClose={() => setShowEmailConfigRequiredModal(false)}
        t={t}
        type={emailModalType}
      />
    </>
  );
};

export default TemplateViewerAdmin;
